import Immutable from 'immutable';
import * as draftjs from 'modules/draftjs/helpers';
import * as lexical from 'modules/Lexical/helpers';
import { call, select } from 'redux-saga/effects';
import guid from 'uuid';

import * as documentsSelectors from 'containers/Documents/selectors';
import { isLexicalMode } from 'containers/EditorToggle/selectors';
import { referenceCitationFactory } from 'factories/document/referenceCitationFactory';
import * as Models from 'models';
import { handleSagaError } from 'services/handleError';
import { findReferenceCitationDuplicateId } from 'utils/findDocumentDuplicateId';

interface AddReferenceCitationToComponentResult {
  newReferenceCitationDocument?: Models.ReferenceCitationMap;
  updatedTextComponent: Models.TextComponentMap;
}

export function* addReferenceCitationToComponent(
  component: Models.TextComponentMap,
  referenceCitation: Models.ReferenceCitationMap,
): Generator<unknown, AddReferenceCitationToComponentResult> {
  try {
    const result = {} as AddReferenceCitationToComponentResult;

    const lexicalMode = yield select(isLexicalMode);

    const storyCardDocuments: ReturnTypeSaga<typeof documentsSelectors.storyCardsAndMagicFormsDocuments> = yield select(
      documentsSelectors.storyCardsAndMagicFormsDocuments,
    );
    const textCollectionsDocuments: ReturnTypeSaga<typeof documentsSelectors.textCollectionsDocuments> = yield select(
      documentsSelectors.textCollectionsDocuments,
    );
    const referenceId = referenceCitation.get('id');
    const existingReference = (storyCardDocuments.get(referenceId) || textCollectionsDocuments.get(referenceId)) as Models.ReferenceCitationMap;
    let newReferenceCitationId: string;
    let textComponent = component;

    if (existingReference) {
      const references: ReturnTypeSaga<typeof documentsSelectors.citationsForAssetsPanel> = yield select(documentsSelectors.citationsForAssetsPanel);
      const duplicateId = findReferenceCitationDuplicateId(existingReference, references);

      if (duplicateId) {
        // use existed reference citation
        newReferenceCitationId = duplicateId;
      } else {
        // create new reference citation document in assets
        // pass the document through the factory to make sure that the document will match its interface
        const newReferenceCitationDocument = Immutable.fromJS(
          referenceCitationFactory(existingReference.set('id', guid()).toJS()),
        ) as Models.ReferenceCitationMap;
        result.newReferenceCitationDocument = newReferenceCitationDocument;
        newReferenceCitationId = newReferenceCitationDocument.get('id');
      }

      // replace id of reference citation from assets collection by correct one
      textComponent = lexicalMode
        ? lexical.replaceReferenceCitationIdOnComponent(textComponent, referenceId, newReferenceCitationId)
        : draftjs.replaceReferenceCitationIdOnComponent(textComponent, referenceId, newReferenceCitationId);
    }

    result.updatedTextComponent = lexicalMode
      ? lexical.updateReferenceCitationsOnComponent(textComponent)
      : draftjs.updateReferenceCitationsOnComponent(textComponent);

    return result;
  } catch (error) {
    yield call(handleSagaError, error, 'ArtboardCell.addReferenceCitationToComponent', 'AddReferenceCitationToComponent');
  }
}
